<template>
  <div class="column justify-center">
    <open-fund-header />
    <div>
      <div v-if="meitavAffiliates" class="r-text-intro">קופת גמל להשקעה - מה יכול להיות?</div>
      <div v-else class="r-text-intro">אפשר לחלום!</div>
      <div class="r-text-headline-paragraph">אם אשים בצד כל חודש:</div>
      <default-slider
        :value="monthlyDeposit"
        :min="100"
        :step="100"
        :max="2000"
        :label-value="monthlyDepositSumSliderLabel"
        color="riseup-blue"
        class="monthly-deposit-slider"
        @input="setMonthlyDeposit"
      />
      <div class="r-text-headline">יכולים להיות לי בעוד:</div>

      <r-switch-buttons
        :options="periodOptions"
        :value="period"
        :lines="1"
        default-color="riseup-beige"
        selected-color="riseup-blue"
        custom-class="justify-center years-buttons"
        default-text-color="riseup-black"
        selected-text-color="riseup-beige"
        size="16px"
        unelevated
        @input="changePeriod" />

      <div class="column justify-center content-center">
        <div class="mask bg-riseup-beige">
          <div id="sun" class="sun bg-riseup-yellow">
            <animated-number
              :value="compoundInterest"
              :formatter="formatAmount"
              :duration="0.8"
              id="calculated-amount"
              class="calculated-amount text-riseup-white r-text-bold"
            />
          </div>
        </div>
      </div>
    </div>

    <default-button v-if="meitavAffiliates" label="הבא >" class="next-button" @click="next"/>
    <default-button v-else label="אני רוצה להמשיך!" class="next-button" @click="next"/>
    <article-button label="איך זה מחושב?" class="how-was-calculated" @click="showHowCalculatedDialog" />
    <Disclaimer />
  </div>
</template>

<script>
import anime from 'animejs/lib/anime.es';
import BaseUI from 'base-ui';
import { mapGetters, mapMutations, mapState } from 'vuex';
import TrackedPage from '@/mixins/TrackedPage';
import DefaultButton from '@/components/styled/DefaultButton';
import DefaultSlider from '@/components/styled/DefaultSlider';
import { COMMISSION_IN_PERCENTAGE, INTEREST_RATE } from '@/constants/meitav-dash/open-fund';
import OpenFundHeader from '@/components/meitav-dash/OpenFundHeader';
import Segment from '@/Segment';
import ProjectionExplained from '@/views/meitav-dash/open-fund/dialogs/ProjectionExplained';
import MoneyFormat from '@/mixins/MoneyFormat';
import ArticleButton from '@/components/styled/ArticleButton';
import RSwitchButtons from '@/components/base/RSwitchButtons';
import Disclaimer from '@/components/meitav-dash/Disclaimer';
import Affiliates from '@/mixins/Affiliates';

export default {
  name: 'Projection',
  components: { Disclaimer, RSwitchButtons, ArticleButton, OpenFundHeader, DefaultSlider, DefaultButton, AnimatedNumber: BaseUI.AnimatedNumber },
  mixins: [TrackedPage, MoneyFormat, Affiliates],
  created() {
    this.setInterestRate(INTEREST_RATE);
  },
  mounted() {
    this.tweenAmountFontSize(this.period);
    this.tweenSun(this.period);
  },
  data() {
    return {
      years: [5, 10, 20],
    };
  },
  computed: {
    ...mapState('lightup/projection', ['monthlyDeposit', 'period']),
    ...mapGetters('lightup/projection', ['compoundInterest']),
    monthlyDepositSumSliderLabel() {
      return `${this.formatAmount(this.monthlyDeposit)}`;
    },
    periodOptions() {
      return this.years.map(year => {
        return {
          value: year,
          label: this.yearLabel(year),
        };
      });
    },
    COMMISSION_IN_PERCENTAGE() {
      return COMMISSION_IN_PERCENTAGE;
    },
  },
  methods: {
    ...mapMutations('lightup/projection', ['setMonthlyDeposit', 'setPeriod', 'setInterestRate']),
    next() {
      this.$router.push('dreams');
    },
    yearLabel(year) {
      if (year >= 20) {
        return `${year} שנה`;
      }
      return `${year} שנים`;
    },
    formatAmount(value) {
      return `${this.formatAmountWithFraction(value, 0)} ₪`;
    },
    changePeriod(newPeriod) {
      Segment.trackUserInteraction('Saving_ChangePeriodClicked', { newPeriod });
      this.setPeriod(newPeriod);
      this.tweenAmountFontSize(newPeriod);
      this.tweenSun(newPeriod);
    },
    tweenSun(newPeriod) {
      anime({ targets: '#sun', duration: 800, marginTop: this.sunMarginTopForPeriod(newPeriod), easing: 'linear' });
    },
    tweenAmountFontSize(newPeriod) {
      anime({ targets: '#calculated-amount', duration: 800, fontSize: this.amountSizeForPeriod(newPeriod), easing: 'linear' });
    },
    amountSizeForPeriod(period) {
      switch (period) {
        case 5: return 20;
        case 10: return 28;
        case 20: return 36;
        default: return 20;
      }
    },
    sunMarginTopForPeriod(period) {
      switch (period) {
        case 5: return 100;
        case 10: return 50;
        case 20: return 20;
        default: return 100;
      }
    },
    showHowCalculatedDialog() {
      Segment.trackUserInteraction('Saving_ExplainProjectionClicked');
      this.$q.dialog({
        component: ProjectionExplained,
        parent: this,
      });
    },
  },
};
</script>

<style lang="scss" scoped>

.monthly-deposit-slider {
  margin: 70px 0 23px 0;
}

.mask {
  display: flex;
  justify-content: center;
  width: 100%;
  height: 170px;
  overflow: hidden;
  box-shadow: 0 10px 11px -16px #111;

  .sun {
    display: flex;
    justify-content: center;
    width: 300px;
    height: 150px;
    border-top-left-radius: 150px;
    border-top-right-radius: 150px;
    border-bottom: 0;
    margin-top: 100px;

    .calculated-amount {
      margin-top: 30px;
      font-size: 20px;
    }
  }
}
.years-buttons {
  margin-top: 16px;
}

.next-button {
  margin-top: 16px;
}

.how-was-calculated {
  margin-bottom: 16px;
}

.toggle-container {
  width: calc(100% - 1px);
  border: black 1px solid;
  border-radius: 3px;
}

</style>
